/**
 * SitoutBadge Component for Poker Reborn
 *
 * Displays "AWAY" badge with optional countdown timer when a player
 * is sitting out. Single-line layout: "AWAY" or "AWAY 1:30".
 *
 * @see docs/screens/room/SEAT.md - Parent SeatView component
 */

import { Container, Graphics, Text } from 'pixi.js';
import { getScaledValue } from '@/utils/responsive';

// =============================================================================
// BREAKPOINTS
// =============================================================================

/**
 * BBS breakpoints for SitoutBadge dimensions.
 * Uses discrete mode (values jump at breakpoints).
 */
const SITOUT_BADGE_BREAKPOINTS = {
  fontSize: { 375: 10, 720: 10, 1440: 10, 1920: 10 },
  gap: { 375: 4, 720: 5, 1440: 6, 1920: 6 },
};

// =============================================================================
// STYLES
// =============================================================================

const COLORS = {
  badge: {
    fill: 0xff6b6b,
    textFill: 0xffffff,
    paddingX: 6,
    paddingY: 4,
    cornerRadius: 4,
  },
  timer: {
    normal: 0xffffff,   // White (> 30s)
    warning: 0xffeb3b,  // Yellow (10-30s)
    critical: 0xf44336, // Red (<= 10s)
  },
};

// =============================================================================
// TYPES
// =============================================================================

export interface SitoutBadgeOptions {
  /** Initial screen width for BBS calculations */
  screenWidth?: number;
}

// =============================================================================
// COMPONENT
// =============================================================================

export class SitoutBadge extends Container {
  private badgeContainer: Container;
  private badgeBg: Graphics;
  private badgeText: Text;
  private countdownText: Text;

  private currentScreenWidth: number;
  private currentSeconds: number | null = null;

  constructor(options: SitoutBadgeOptions = {}) {
    super();

    this.currentScreenWidth = options.screenWidth ?? 1920;

    const fontSize = this.bbs(SITOUT_BADGE_BREAKPOINTS.fontSize);

    // Badge container holds background + both texts
    this.badgeContainer = new Container();
    this.addChild(this.badgeContainer);

    // Badge background
    this.badgeBg = new Graphics();
    this.badgeContainer.addChild(this.badgeBg);

    // "AWAY" label
    this.badgeText = new Text({
      text: 'AWAY',
      style: {
        fill: COLORS.badge.textFill,
        fontSize,
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: 'bold',
      },
    });
    this.badgeText.anchor.set(0, 0.5);
    this.badgeContainer.addChild(this.badgeText);

    // Countdown (same line, right of "AWAY")
    this.countdownText = new Text({
      text: '',
      style: {
        fill: COLORS.timer.normal,
        fontSize,
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: 'bold',
      },
    });
    this.countdownText.anchor.set(0, 0.5);
    this.countdownText.visible = false;
    this.badgeContainer.addChild(this.countdownText);

    // Initial render
    this.updateLayout();
  }

  // ===========================================================================
  // BBS HELPER
  // ===========================================================================

  private bbs(breakpoints: Record<number, number>): number {
    return getScaledValue(this.currentScreenWidth, breakpoints, { discrete: true });
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /**
   * Set the countdown timer.
   * @param seconds - Remaining seconds, or null to hide timer
   */
  setCountdown(seconds: number | null): void {
    this.currentSeconds = seconds;

    if (seconds === null || seconds <= 0) {
      this.countdownText.visible = false;
      this.updateLayout();
      return;
    }

    // Format as M:SS
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    this.countdownText.text = `${String(minutes)}:${secs.toString().padStart(2, '0')}`;

    // Color based on remaining time
    let color: number;
    if (seconds > 30) {
      color = COLORS.timer.normal;
    } else if (seconds > 10) {
      color = COLORS.timer.warning;
    } else {
      color = COLORS.timer.critical;
    }
    this.countdownText.style.fill = color;

    this.countdownText.visible = true;
    this.updateLayout();
  }

  /**
   * Resize for new screen width.
   */
  resize(screenWidth: number): void {
    if (screenWidth === this.currentScreenWidth) return;
    this.currentScreenWidth = screenWidth;

    const fontSize = this.bbs(SITOUT_BADGE_BREAKPOINTS.fontSize);
    this.badgeText.style.fontSize = fontSize;
    this.countdownText.style.fontSize = fontSize;

    this.updateLayout();
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private updateLayout(): void {
    const { paddingX, paddingY } = COLORS.badge;
    const gap = this.bbs(SITOUT_BADGE_BREAKPOINTS.gap);

    // Measure content width
    let contentWidth = this.badgeText.width;
    if (this.countdownText.visible) {
      contentWidth += gap + this.countdownText.width;
    }

    const badgeWidth = contentWidth + paddingX * 2;
    const badgeHeight = this.badgeText.height + paddingY * 2;

    // Draw background centered at origin
    this.badgeBg.clear();
    this.badgeBg.roundRect(
      -badgeWidth / 2,
      -badgeHeight / 2,
      badgeWidth,
      badgeHeight,
      COLORS.badge.cornerRadius
    );
    this.badgeBg.fill(COLORS.badge.fill);

    // Position texts inside badge
    const contentStartX = -contentWidth / 2;
    this.badgeText.x = contentStartX;
    this.badgeText.y = 0;

    if (this.countdownText.visible) {
      this.countdownText.x = contentStartX + this.badgeText.width + gap;
      this.countdownText.y = 0;
    }
  }
}
